import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { userAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { Spinner, EmptyState, Pagination } from '../../components/common/Misc';
import Button from '../../components/common/Button';
import styles from './AdminDashboard.module.css';

const AdminUsers = () => {
  const { user: me } = useAuth();
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [pages,   setPages]   = useState(1);
  const [filter,  setFilter]  = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (filter) params.role = filter;
      const res = await userAPI.getAll(params);
      setUsers(res.data.data);
      setPages(res.data.pages);
    } finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleToggle = async (id) => {
    try { await userAPI.toggle(id); toast.success('User status updated'); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleRoleChange = async (id, role) => {
    try { await userAPI.changeRole(id, { role }); toast.success('Role updated'); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try { await userAPI.delete(id); toast.success('User deleted'); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>User management</h2>
        <div style={{ display:'flex', gap:'.4rem' }}>
          {['', 'admin', 'staff'].map(r => (
            <button key={r}
              onClick={() => { setFilter(r); setPage(1); }}
              style={{
                padding:'.3rem .75rem', borderRadius:'100px', border:'1.5px solid', fontSize:'.78rem',
                fontWeight:500, cursor:'pointer', textTransform:'capitalize',
                borderColor: filter === r ? 'var(--color-primary)' : 'var(--color-border)',
                background:  filter === r ? 'var(--color-primary)' : 'none',
                color:       filter === r ? '#fff' : 'var(--color-text-secondary)',
              }}
            >{r || 'All'}</button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : users.length === 0
        ? <EmptyState icon="👥" title="No users found" />
        : (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Status</th><th>Joined</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id}>
                    <td>
                      <strong>{u.name}</strong>
                      {u._id === me?._id && <span style={{ fontSize:'.7rem', color:'var(--color-primary)', marginLeft:'.35rem' }}>(you)</span>}
                    </td>
                    <td>{u.email}</td>
                    <td>{u.phone || ''}</td>
                    <td>
                      {u._id === me?._id
                        ? <span style={{ textTransform:'capitalize', fontWeight:500 }}>{u.role}</span>
                        : (
                          <select
                            value={u.role}
                            onChange={e => handleRoleChange(u._id, e.target.value)}
                            style={{ fontSize:'.8rem', padding:'.2rem .4rem', borderRadius:4, border:'1px solid var(--color-border)', background:'var(--color-surface)' }}
                          >
                            <option value="staff">Staff</option>
                            <option value="admin">Admin</option>
                          </select>
                        )
                      }
                    </td>
                    <td>
                      <span style={{ color: u.is_active ? 'var(--color-success)' : 'var(--color-danger)', fontSize:'.8rem', fontWeight:500 }}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ fontSize:'.8rem', color:'var(--color-text-muted)' }}>
                      {format(new Date(u.createdAt), 'dd MMM yyyy')}
                    </td>
                    <td>
                      {u._id !== me?._id && (
                        <div className={styles.actionBtns}>
                          <Button size="sm" variant={u.is_active ? 'secondary' : 'success'} onClick={() => handleToggle(u._id)}>
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => handleDelete(u._id)}>Del</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
      <Pagination page={page} pages={pages} onChange={setPage} />
    </div>
  );
};

export default AdminUsers;
